/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { HttpErrorResponse } from '@angular/common/http';
import {Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ProspettoApiClient } from '@ideapa/app-api-client';
import { NavigationFacade } from '@ideapa/app-navigation';
import { NgrxLoaderFacade } from '@ideapa/app-ngrx-loader';
import { Ente } from '@ideapa/app-shared-ente';
import { ToastService } from '@ideapa/app-ui-kit';
import { LoginStatusEnum, RoleCode } from '@ideapa/common-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap/modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch } from '@ngrx/router-store/data-persistence';
import moment from 'moment';
import { NgxPermissionsService } from 'ngx-permissions';
import {
  catchError,
  EMPTY,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { AuthService } from '../auth.service';
import { ChangePasswordModalContainer } from '../containers/change-password-modal/change-password-modal.container';
import { SelectModuloModalContainer } from '../containers/select-modulo-modal/select-modulo-modal.container';
import * as AuthActions from './auth.actions';
import { AuthFacade } from './auth.facade';

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private permissionsService: NgxPermissionsService,
    private authService: AuthService,
    private authFacade: AuthFacade,
    private router: Router,
    private loader: NgrxLoaderFacade,
    private toastService: ToastService,
    @Inject(NgbModal) private modalService: NgbModal,
    private navigationFacade: NavigationFacade,
    private prospettoService: ProspettoApiClient
  ) {}

  failure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          // AuthActions.loginFailure,
          // AuthActions.forgotPasswordFailure,
          AuthActions.resetPasswordFailure,
          AuthActions.verifyMAOTPFailure,
          AuthActions.verifyEmailOTPFailure
        ),
        tap((a) => this.toastService.errorFeedback(a.error))
      ),
    { dispatch: false }
  );

  loginAAD$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithAAD),
      switchMap((a) => {
        this.loader.showLoader(a);
        return this.authService.loginAAD().pipe(
          switchMap((result) => {
            if (result.isAuthenticated) {
              return this.authService.switchToken(
                result.accessToken,
                a.rememberMe
              );
            } else {
              this.loader.hideLoader(a);
              return EMPTY;
            }
          }),
          map((result) => {
            this.loader.hideLoader(a);
            return AuthActions.loginWithAADSuccess({ result });
          }),
          catchError((error) => {
            this.loader.hideLoader(a);
            return of(
              AuthActions.loginWithAADFailure({
                error: error.error?.message || error.message,
              })
            );
          })
        );
      })
    )
  );

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      switchMap((a) => {
        this.loader.showLoader(a);
        // Your custom service 'load' logic goes here. For now just return a success action...
        return this.authService
          .login(a.username, a.password, a.rememberMe, a.newPassword)
          .pipe(
            switchMap((result) => {
              this.loader.hideLoader(a);
              // console.log('Login result:', result);
              switch (result.loginStatus) {
                case LoginStatusEnum.completed:
                  if (result.token) {
                    return of(
                      AuthActions.loginSuccess({ result: result.token })
                    );
                  } else {
                    return of(
                      AuthActions.loginFailure({
                        error: 'Accesso fallito. Token mancante.',
                      })
                    );
                  }
                case LoginStatusEnum.needChooseMfaMethod:
                  return of(
                    AuthActions.chooseMFA({
                      result: result,
                    })
                  );
                case LoginStatusEnum.needMASetup:
                  return of(
                    AuthActions.setupMA({
                      result: result,
                    })
                  );
                case LoginStatusEnum.needMAVerification:
                  return of(
                    AuthActions.insertMAOTP({
                      result: result,
                    })
                  );
                case LoginStatusEnum.needEmailOtpCodeVerification:
                  return of(
                    AuthActions.insertEmailOTP({
                      result: result,
                    })
                  );
                default:
                  return of(
                    AuthActions.loginFailure({
                      error: 'Accesso fallito. Riprova.',
                    })
                  );
              }
            }),
            catchError((error: HttpErrorResponse) => {
              this.loader.hideLoader(a);
              if (error.status === 417) {
                this.authFacade.loginFormInitializeWithTmpPassword(
                  a.username,
                  a.password
                );
                console.log(
                  'Navigating to login with temporary password due to 417 error'
                );
                this.router.navigateByUrl('auth/login');

                return of(AuthActions.setUserMustResetPassword());
              }

              return of(
                AuthActions.loginFailure({
                  error: error.error?.message || error.message,
                })
              );
            })
          );
      })
    )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AuthActions.loginSuccess,
          AuthActions.loginWithAADSuccess,
          AuthActions.verifyMAOTPSuccess,
          AuthActions.verifyEmailOTPSuccess
        ),
        tap(() => {
          // this.authService.logoutAAD();
          const url = this.authService.popTmpUrl() || '/';
          console.log('Login successful, navigating to:', url);
          this.router.navigateByUrl(url);
        })
      ),
    { dispatch: false }
  );

  setupMA$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.setupMA),
        tap(() => {
          console.log('Navigating to MA setup');
          this.router.navigateByUrl('/auth/microsoft-authenticator-setup');
        })
      ),
    { dispatch: false }
  );

  insertMAOTP$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.insertMAOTP),
        tap(() => {
          console.log('Navigating to MA OTP verification');
          this.router.navigateByUrl('/auth/microsoft-authenticator-verify');
        })
      ),
    { dispatch: false }
  );

  verifyMAOTP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyMAOTP),
      switchMap((a) => {
        this.loader.showLoader(a);
        return this.authService.verifyMAOTP(a.otpCode, a.loginResponse).pipe(
          map((result) => {
            this.loader.hideLoader(a);
            return AuthActions.verifyMAOTPSuccess({ result });
          }),
          catchError((error) => {
            this.loader.hideLoader(a);
            return of(
              AuthActions.verifyMAOTPFailure({
                error: error.error?.message || error.message,
              })
            );
          })
        );
      })
    )
  );

  chooseMFA$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.chooseMFA),
        tap(() => {
          console.log('Navigating to MFA method selection');
          this.router.navigateByUrl('/auth/choose-mfa-method');
        })
      ),
    { dispatch: false }
  );

  proceedToMFAMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.proceedToMFAMethod),
      switchMap((a) => {
        this.loader.showLoader(a);
        return this.authService
          .proceedToMFAMethod(a.loginData, a.method, a.loginResponse)
          .pipe(
            switchMap((result) => {
              this.loader.hideLoader(a);
              // console.log('Login result:', result);
              switch (result.loginStatus) {
                case LoginStatusEnum.needMASetup:
                  return of(
                    AuthActions.setupMA({
                      result: result,
                    })
                  );
                case LoginStatusEnum.needEmailOtpCodeVerification:
                  return of(
                    AuthActions.insertEmailOTP({
                      result: result,
                    })
                  );
                default:
                  return of(
                    AuthActions.loginFailure({
                      error: "Si è verificato un errore. Ripeti l'accesso.",
                    })
                  );
              }
              // return AuthActions.proceedToMFAMethodSuccess();
            }),
            catchError((error) => {
              this.loader.hideLoader(a);
              return of(
                AuthActions.proceedToMFAMethodFailure({
                  error: error.error?.message || error.message,
                })
              );
            })
          );
      })
    )
  );

  insertEmailOTP$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.insertEmailOTP),
        tap(() => {
          console.log('Navigating to Email OTP verification');
          this.router.navigateByUrl('/auth/email-otp-verify');
        })
      ),
    { dispatch: false }
  );

  verifyEmailOTP$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyEmailOTP),
      switchMap((a) => {
        this.loader.showLoader(a);
        return this.authService.verifyEmailOTP(a.otpCode, a.loginResponse).pipe(
          map((result) => {
            this.loader.hideLoader(a);
            return AuthActions.verifyEmailOTPSuccess({ result });
          }),
          catchError((error) => {
            this.loader.hideLoader(a);
            return of(
              AuthActions.verifyEmailOTPFailure({
                error: error.error?.message || error.message,
              })
            );
          })
        );
      })
    )
  );

  missingLoginResponse$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.missingLoginResponse),
        tap(() => {
          this.toastService.errorFeedback(
            'È necessario eseguire di nuovo il login.'
          );
          console.log('Navigating to login due to missing login response');
          this.router.navigateByUrl('/auth/login');
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      switchMap((a) => {
        this.loader.showLoader(a);
        return this.authService.logout().pipe(
          map(() => {
            this.loader.hideLoader(a);
            return AuthActions.logoutSuccess();
          }),
          catchError((err) => {
            this.loader.hideLoader(a);
            return of(AuthActions.logoutFailure(err));
          })
        );
      })
    )
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap((res) => {
          // this.authService.setLocalAccessToken(res.access_token);
          console.log('Logout successful, navigating to login');
          this.router.navigateByUrl('/auth/login');

          this.navigationFacade.init();
        })
      ),
    { dispatch: false }
  );

  sessionExpired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sessionExpired),
        tap(() => {
          this.toastService.errorFeedback(
            'La sessione è scaduta, sarai rediretto alla pagina di login.'
          );
          // this.authService.logoutLocal();
          setTimeout(() => {
            this.authFacade.logout();
            this.loader.forceHideLoader();
            this.modalService.dismissAll();
          }, 3000);
        })
      ),
    { dispatch: false }
  );

  refresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forceRefresh),
      mergeMap(() => {
        return from(this.authService.refreshTokens()).pipe(
          map((response) =>
            AuthActions.forceRefreshSucces({ result: response })
          ),
          catchError((err) => of(AuthActions.forceRefreshFail({ error: err })))
        );
      })
    )
  );

  getLoggedUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.getLoggedUserSuccess),
        tap(() => {
          // console.log('Utente loggato recuperato con successo');
        })
      ),
    { dispatch: false }
  );

  getLoggedUserFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.getLoggedUserFail),
        tap(() => {
          console.error(
            'Navigating to forbidden page due to getLoggedUser failure'
          );
          this.router.navigateByUrl('/forbidden');
        })
      ),
    { dispatch: false }
  );

  checkAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.checkAADAuth),
      mergeMap(() => {
        return from(this.authService.checkAADAuth()).pipe(
          map((response) =>
            AuthActions.checkAuthSuccess({ loggedIn: !!response })
          ),
          catchError((err) => of(AuthActions.checkAuthFail({ error: err })))
        );
      })
    )
  );

  checkAuthFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.checkAuthFail),
        tap(() => {
          console.error('Navigating to login due to checkAuth failure');
          this.router.navigateByUrl('login');
        })
      ),
    { dispatch: false }
  );

  getLoggedUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getLoggedUser),
      fetch({
        run: () => {
          // Your custom service 'load' logic goes here. For now just return a success action...
          return this.authService.me().pipe(
            map((loggedUser) => {
              return AuthActions.getLoggedUserSuccess({ loggedUser });
            })
          );
        },
        onError: (action, error) => {
          return AuthActions.getLoggedUserFail({ error: error.message });
        },
      })
    )
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      fetch({
        run: (a) => {
          this.loader.showLoader(a);
          return this.authService.forgotPassword(a.username).pipe(
            map(() => {
              this.loader.hideLoader(a);
              return AuthActions.forgotPasswordSuccess();
            })
          );
        },
        onError: (action, error) => {
          this.loader.hideLoader(action);
          return AuthActions.forgotPasswordFailure({
            error: error.message ? error.message : error,
          });
        },
      })
    )
  );

  forgotPasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.forgotPasswordSuccess),
        tap(() =>
          this.toastService.successFeedback(
            'Riceverai una email con le istruzioni per il reset della password.'
          )
        )
      ),
    { dispatch: false }
  );

  resetPassword = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPassword),
      fetch({
        run: (a) => {
          this.loader.showLoader(a);
          // Your custom service 'load' logic goes here. For now just return a success action...
          return of(a).pipe(
            switchMap((action) => {
              return this.authService
                .resetPassword(action.token, action.newPassword)
                .pipe(
                  map(() => {
                    this.loader.hideLoader(action);
                    console.log('Navigating to login after password reset');
                    this.router.navigateByUrl('auth/login');
                    return AuthActions.resetPasswordSuccess();
                  })
                );
            })
          );
        },
        onError: (action, error) => {
          this.loader.hideLoader(action);
          return AuthActions.resetPasswordFailure({
            error: error.message ? error.message : error,
          });
        },
      })
    )
  );

  showSelectModuloPopup$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.showSelectModuloPopup),
        withLatestFrom(
          this.authFacade.modulo$,
          this.authFacade.ente$,
          this.authFacade.anno$,
          this.authFacade.selectLoggedUser$
        ),
        switchMap(([a, modulo, ente, anno, user]) => {
          const dialogRef = this.modalService.open(SelectModuloModalContainer, {
            backdrop: 'static',
            size: 'md',
          });

          if (a.modulo) {
            let enteToSelect = ente;
            if (
              a.modulo.entiAssociati?.find((e) => e.codice === ente?.codice) ==
              null
            ) {
              enteToSelect = null;
            }

            this.authFacade.initSelectModuloForm(
              enteToSelect,
              anno || moment().subtract(1, 'y').year()
            );

            const instance =
              dialogRef.componentInstance as SelectModuloModalContainer;
            instance.showWarning = !!modulo;
            instance.enti = a.modulo.entiAssociati || [];
          } else if (modulo && ente && anno) {
            this.authFacade.initSelectModuloForm(ente, anno);

            const instance =
              dialogRef.componentInstance as SelectModuloModalContainer;
            instance.showWarning = !!modulo;
            instance.enti = modulo.entiAssociati || [];
          }

          return from(dialogRef.result).pipe(
            tap((result: { ente: Ente; anno: number } | undefined) => {
              if (result) {
                if (a.modulo) this.authFacade.selectModulo(a.modulo, modulo);

                this.authFacade.selectAnno(result.anno, anno);
                if (user?.role?.code != RoleCode.CompanyUser) {
                  this.authFacade.selectEnte(result.ente, ente);
                }
              }
            })
          );
        })
      ),
    { dispatch: false }
  );

  showChangePassword$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.showChangePasswordPopup),
        switchMap(() => {
          const dialogRef = this.modalService.open(
            ChangePasswordModalContainer,
            {
              backdrop: 'static',
              size: 'sm',
            }
          );

          return from(dialogRef.result).pipe(
            tap(
              (
                result: { password: string; newPassword: string } | undefined
              ) => {
                if (result) {
                  this.authFacade.changePassword(
                    result.password,
                    result.newPassword
                  );
                }
              }
            )
          );
        })
      ),
    { dispatch: false }
  );

  changePassword = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changePassword),
      fetch({
        run: (a) => {
          this.loader.showLoader(a);
          // Your custom service 'load' logic goes here. For now just return a success action...
          return of(a).pipe(
            switchMap((action) => {
              return this.authService
                .changePassword(action.password, action.newPassword)
                .pipe(
                  map(() => {
                    this.loader.hideLoader(action);
                    this.toastService.successFeedback(
                      'La password è stata modificata con successo'
                    );
                    return AuthActions.changePasswordSuccess();
                  })
                );
            })
          );
        },
        onError: (action, error) => {
          this.loader.hideLoader(action);
          return AuthActions.changePasswordFailure({
            error: error.message ? error.message : error,
          });
        },
      })
    )
  );

  selectModulo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.selectModulo),
        tap((a) => {
          if (a.modulo.id !== a.prevModulo?.id) {
            this.navigationFacade.closeModuloRelatedTabs();
          }
        })
      ),
    { dispatch: false }
  );

  clearModulo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.clearModulo),
        tap(() => {
          this.navigationFacade.closeModuloRelatedTabs();
          this.navigationFacade.closeEnteAnnoRelatedTabs();
        })
      ),
    { dispatch: false }
  );

  selectEnte$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.selectEnte),
        tap((a) => {
          if (a.ente.id !== a.prevEnte?.id) {
            this.navigationFacade.closeEnteAnnoRelatedTabs();
          }
        })
      ),
    { dispatch: false }
  );

  selectAnno$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.selectAnno),
        tap((a) => {
          if (a.anno !== a.prevAnno) {
            this.navigationFacade.closeEnteAnnoRelatedTabs();
          }
        })
      ),
    { dispatch: false }
  );
}
